import { useSeoMeta } from '@unhead/react';
import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useMerchants } from '@/hooks/useMerchants';
import { MerchantMap } from '@/components/MerchantMap';
import { MerchantList } from '@/components/MerchantList';
import { MerchantDetail } from '@/components/MerchantDetail';
import { FilterBar } from '@/components/FilterBar';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LoginArea } from '@/components/auth/LoginArea';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { SponsoredBadge } from '@/components/SponsoredBadge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isGrantIssuer } from '@/lib/businessVerify';
import { ListBusinessDialog } from '@/components/ListBusinessDialog';
import { BchLaunchPromo } from '@/components/BchLaunchPromo';
import type { Merchant } from '@/lib/merchants';
import { isActiveWithin, getMerchantStatus } from '@/lib/merchants';
import { StatusBadge } from '@/components/StatusBadge';
import { Map, List, RefreshCw, MapPin, Zap, Store, ShieldCheck, Globe } from 'lucide-react';

const Index = () => {
  useSeoMeta({
    title: 'BCH Local — Find Bitcoin Cash Merchants Near You',
    description: 'Discover Bitcoin Cash merchants worldwide. An interactive map and directory — all listings come from BCHPAY.Cash merchants who have enabled BCH Local discovery.',
  });

  const { merchants, isLoading, isError, refetch } = useMerchants();
  const isMobile = useIsMobile();
  const { user } = useCurrentUser();
  const isIssuer = !!user && isGrantIssuer(user.pubkey);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  // Filtered merchants
  const filteredMerchants = useMemo(() => {
    let result = merchants;

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        m =>
          m.name.toLowerCase().includes(q) ||
          m.summary.toLowerCase().includes(q) ||
          m.categories.some(c => c.toLowerCase().includes(q)) ||
          m.geohash.toLowerCase().includes(q),
      );
    }

    // Category filter
    if (selectedCategory) {
      result = result.filter(m =>
        m.categories.some(c => c.toLowerCase() === selectedCategory.toLowerCase()),
      );
    }

    // Active only
    if (activeOnly) {
      result = result.filter(m => isActiveWithin(m, 24));
    }

    // Verified only
    if (verifiedOnly) {
      result = result.filter(m => m.verified);
    }

    return result;
  }, [merchants, searchQuery, selectedCategory, activeOnly, verifiedOnly]);

  // Merchants with location for map
  const mappableMerchants = useMemo(
    () => filteredMerchants.filter(m => m.location !== null),
    [filteredMerchants],
  );

  const handleSelectMerchant = useCallback((merchant: Merchant) => {
    setSelectedMerchant(merchant);
    setSheetOpen(true);
  }, []);

  // Stats
  const totalMerchants = merchants.length;
  const activeMerchants = merchants.filter(m => isActiveWithin(m, 24)).length;
  const verifiedMerchants = merchants.filter(m => m.verified).length;
  const withLocation = merchants.filter(m => m.location !== null).length;

  return (
    <div className="min-h-screen bg-background">
      {/* Header / Navbar */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="container flex items-center justify-between h-14 px-4 mx-auto max-w-7xl">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center size-8 rounded-lg bg-bch">
              <MapPin className="size-4 text-bch-foreground" />
            </div>
            <span className="font-bold text-base sm:text-lg">BCH Local</span>
          </div>
          <div className="flex items-center gap-2">
            {isIssuer && (
              <Button asChild variant="ghost" size="sm" className="gap-1.5 rounded-full">
                <Link to="/admin/verification" aria-label="Verification admin">
                  <ShieldCheck className="size-4 text-bch" />
                  <span className="hidden sm:inline">Admin</span>
                </Link>
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="size-9 rounded-full"
              onClick={() => refetch()}
              aria-label="Refresh"
            >
              <RefreshCw className="size-4" />
            </Button>
            <ThemeToggle />
            <LoginArea className="max-w-52" />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative isolate overflow-hidden border-b">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-bch/10 via-bch/5 to-transparent" />
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,var(--color-bch)/0.12,transparent)]" />

        <div className="container px-4 mx-auto max-w-7xl py-10 sm:py-14">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3">
              Find BCH Merchants{' '}
              <span className="text-bch">Near You</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-lg mx-auto">
              Discover businesses accepting Bitcoin Cash worldwide, powered by the Nostr network
            </p>
          </div>

          {/* Stats */}
          {!isLoading ? (
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center justify-center size-8 rounded-full bg-bch/10">
                  <Store className="size-4 text-bch" />
                </div>
                <div>
                  <div className="font-semibold">{totalMerchants}</div>
                  <div className="text-xs text-muted-foreground">Merchants</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center justify-center size-8 rounded-full bg-bch/10">
                  <ShieldCheck className="size-4 text-bch" />
                </div>
                <div>
                  <div className="font-semibold">{verifiedMerchants}</div>
                  <div className="text-xs text-muted-foreground">Verified</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center justify-center size-8 rounded-full bg-bch/10">
                  <Zap className="size-4 text-bch" />
                </div>
                <div>
                  <div className="font-semibold">{activeMerchants}</div>
                  <div className="text-xs text-muted-foreground">Active (24h)</div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center justify-center size-8 rounded-full bg-bch/10">
                  <MapPin className="size-4 text-bch" />
                </div>
                <div>
                  <div className="font-semibold">{withLocation}</div>
                  <div className="text-xs text-muted-foreground">On Map</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-6">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="size-8 rounded-full" />
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-8" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTAs */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="sm" className="gap-2 rounded-full bg-bch hover:bg-bch/90 text-bch-foreground" onClick={() => setListOpen(true)}>
              <Store className="size-4" />
              <span>Add your business</span>
            </Button>
            <Button asChild variant="outline" size="sm" className="gap-2 rounded-full border-bch/30 hover:bg-bch/5 hover:border-bch/50">
              <Link to="/get-verified">
                <ShieldCheck className="size-4 text-bch" />
                <span>Own a business? <span className="font-semibold text-bch">Get verified</span></span>
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container px-4 mx-auto max-w-7xl py-6">
        {/* BCH Launch promotion */}
        <BchLaunchPromo className="mb-4" />

        {isError && (
          <Card className="mb-4 border-destructive/50 bg-destructive/5">
            <CardContent className="py-3 px-4 text-sm text-destructive flex items-center gap-2">
              <span>Failed to load some merchant data. Check relay connections or try refreshing.</span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="list" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            {/* Filter bar */}
            <div className="flex-1 min-w-0">
              <FilterBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                activeOnly={activeOnly}
                onActiveOnlyChange={setActiveOnly}
                verifiedOnly={verifiedOnly}
                onVerifiedOnlyChange={setVerifiedOnly}
                totalCount={merchants.length}
                filteredCount={filteredMerchants.length}
              />
            </div>

            {/* View toggle */}
            <TabsList className="shrink-0">
              <TabsTrigger value="map">
                <Map className="size-4" />
                <span className="hidden sm:inline">Map</span>
              </TabsTrigger>
              <TabsTrigger value="list">
                <List className="size-4" />
                <span className="hidden sm:inline">List</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Map View */}
          <TabsContent value="map">
            {isLoading ? (
              <Skeleton className="w-full rounded-xl" style={{ height: isMobile ? '400px' : '560px' }} />
            ) : mappableMerchants.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-16 px-8 text-center">
                  <MapPin className="size-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    No merchants with map coordinates found. Try adjusting your filters.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <MerchantMap
                merchants={mappableMerchants}
                onSelectMerchant={handleSelectMerchant}
                selectedMerchant={selectedMerchant}
                className={isMobile ? 'h-[400px]' : 'h-[560px]'}
              />
            )}

            {/* Below-map merchant cards (for quick browsing) */}
            {!isLoading && filteredMerchants.length > 0 && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredMerchants.slice(0, 6).map(merchant => (
                  <div
                    key={merchant.event.id}
                    className="cursor-pointer"
                    onClick={() => handleSelectMerchant(merchant)}
                  >
                    <Card className={`hover:shadow-md transition-all h-full ${merchant.sponsored ? 'border-amber-300/50 bg-amber-50/30 hover:bg-amber-50/50 dark:border-amber-700/30 dark:bg-amber-950/10 dark:hover:bg-amber-950/20' : merchant.verified ? 'border-bch/40 bg-bch/[0.04] ring-1 ring-bch/20 hover:bg-bch/[0.07] dark:bg-bch/[0.06]' : 'hover:bg-accent/30'}`}>
                      <CardContent className="py-3 px-4">
                        {merchant.sponsored && (
                          <div className="mb-1">
                            <SponsoredBadge size="sm" />
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="font-semibold text-sm truncate">{merchant.name}</span>
                          {merchant.verified && <VerifiedBadge size="sm" label />}
                          <StatusBadge status={getMerchantStatus(merchant)} size="dot" />
                        </div>
                        {merchant.summary && (
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {merchant.summary}
                          </p>
                        )}
                        {!merchant.location && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground/70">
                            <Globe className="size-3" />
                            <span>Online only</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* List View */}
          <TabsContent value="list">
            <div className={isMobile ? '' : 'max-w-2xl'}>
              <MerchantList
                merchants={filteredMerchants}
                isLoading={isLoading}
                selectedMerchant={selectedMerchant}
                onSelectMerchant={handleSelectMerchant}
              />
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12">
        <div className="container px-4 mx-auto max-w-7xl py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center size-5 rounded bg-bch">
                <MapPin className="size-3 text-bch-foreground" />
              </div>
              <span>BCH Local</span>
              <span className="text-border">|</span>
              <span>Powered by Nostr</span>
            </div>
            <div className="flex items-center gap-3">
              <span>Listings powered by <a href="https://bchpay.cash" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">BCHPAY.Cash</a></span>
              <span className="text-border">|</span>
              <a href="https://shakespeare.diy" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
                Vibed with Shakespeare
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Merchant Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side={isMobile ? 'bottom' : 'right'} className={isMobile ? 'max-h-[80vh] rounded-t-2xl' : ''}>
          <SheetHeader>
            <SheetTitle>Merchant Details</SheetTitle>
            <SheetDescription>
              View details about this BCH merchant
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-4 overflow-y-auto">
            {selectedMerchant && (
              <MerchantDetail merchant={selectedMerchant} />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* List your business dialog */}
      <ListBusinessDialog open={listOpen} onOpenChange={setListOpen} />
    </div>
  );
};

export default Index;
